/**
 * Sheet Metal Size Selector
 *
 * Pure JS — no Easify dependency. Dimension inputs are rendered by
 * sheet-metal-form.liquid inside the native product form.
 *
 * 1. Reads width/length from our own inputs
 * 2. Calculates area (width × length)
 * 3. Submits directly via Cart AJAX API (bypasses Easify's form interception)
 * 4. Dispatches theme events to open cart drawer / update cart count
 * 5. Validates dimensions before submission (whole numbers, 1–25)
 */
(function SheetMetalController() {
  'use strict';

  var CONFIG = { MIN: 1, MAX: 25 };
  var INSUFFICIENT_STOCK_ERROR = "There isn't enough stock available for these dimensions. Please enter a smaller size or choose another thickness.";
  var ROLLBACK_ERROR = 'We could not restore your basket. Please check your basket before trying again.';

  if (window.KernowcraftSheetMetalController) {
    window.KernowcraftSheetMetalController.init(document);
    return;
  }

  var initializedContainers = new WeakSet();

  function init(root) {
    var containers = [];
    var scope = root && root.querySelectorAll ? root : document;

    if (root && root.nodeType === 1 && root.matches('[data-sheet-metal-form]')) {
      containers.push(root);
    }

    var descendants = scope.querySelectorAll('[data-sheet-metal-form]');
    for (var i = 0; i < descendants.length; i++) {
      containers.push(descendants[i]);
    }

    for (var j = 0; j < containers.length; j++) {
      initContainer(containers[j]);
    }
  }

  function initContainer(container) {
    if (initializedContainers.has(container)) return;

    var formEl = container.closest('form[is="product-form"]');
    if (!formEl) return;

    var widthInput = container.querySelector('[data-sheet-metal-width]');
    var lengthInput = container.querySelector('[data-sheet-metal-length]');
    var quantityInput = container.querySelector('[data-sheet-metal-quantity]');
    var priceDisplay = container.querySelector('[data-sheet-metal-price]');
    var unitPriceDisplay = container.querySelector('[data-sheet-metal-unit-price]');

    if (!widthInput || !lengthInput || !quantityInput) return;

    initializedContainers.add(container);

    var currentPriceCents = parseInt(container.getAttribute('data-current-price'), 10) || 0;
    var isSubmitting = false;

    widthInput.addEventListener('input', syncValues);
    lengthInput.addEventListener('input', syncValues);

    formEl.addEventListener('variant:change', onVariantChange);
    formEl.addEventListener('submit', onSubmit, { capture: true });

    // Easify may intercept click events on the submit button before they
    // become form submit events. Catch clicks in capture phase too.
    var submitBtn = formEl.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        onSubmit(e);
      }, { capture: true });
    }

    hideEasify();

  /**
   * Easify's app embed injects its UI dynamically inside .tpo_option-set-wrapper.
   * Hide the container AND disable all its inputs.
   */
  function hideEasify() {
    var attempts = 0;

    function poll() {
      var wrapper = formEl.querySelector('.tpo_option-set-wrapper');
      if (wrapper) {
        wrapper.style.display = 'none';

        var inputs = wrapper.querySelectorAll('input, select, textarea');
        for (var i = 0; i < inputs.length; i++) {
          inputs[i].disabled = true;
          inputs[i].removeAttribute('required');
        }

        if (formEl.id) {
          var linked = document.querySelectorAll(
            'input[form="' + formEl.id + '"][class*="tpo"],' +
            'input[form="' + formEl.id + '"][name*="Dimensions for"]'
          );
          for (var j = 0; j < linked.length; j++) {
            linked[j].disabled = true;
          }
        }
        return;
      }
      if (++attempts < 50) {
        setTimeout(poll, 200);
      }
    }

    poll();
  }

  function syncValues() {
    var w = parseInt(widthInput.value, 10) || 0;
    var l = parseInt(lengthInput.value, 10) || 0;
    var area = w * l;

    quantityInput.value = area > 0 ? String(area) : '1';

    if (priceDisplay) {
      if (area > 0 && currentPriceCents > 0) {
        var total = (currentPriceCents * area) / 100;
        priceDisplay.textContent = 'Your Total Price Is: \u00A3' + total.toFixed(2);
      } else {
        priceDisplay.textContent = '';
      }
    }
  }

  function onVariantChange(event) {
    if (event.detail && event.detail.variant) {
      currentPriceCents = event.detail.variant.price;

      if (unitPriceDisplay) {
        unitPriceDisplay.textContent = 'Price per cm\u00B2: \u00A3' + (currentPriceCents / 100).toFixed(2);
      }
    }
    syncValues();
  }

  function validate() {
    var errors = [];
    var rawW = widthInput.value.trim();
    var rawL = lengthInput.value.trim();
    var w = parseFloat(rawW);
    var l = parseFloat(rawL);

    if (rawW === '' || rawL === '') {
      errors.push('Please enter both width and length.');
      return errors;
    }

    if (!Number.isInteger(w)) {
      errors.push('Width must be a whole number (no decimals).');
    }
    if (!Number.isInteger(l)) {
      errors.push('Length must be a whole number (no decimals).');
    }
    if (Number.isInteger(w) && (w < CONFIG.MIN || w > CONFIG.MAX)) {
      errors.push('Width must be between ' + CONFIG.MIN + ' and ' + CONFIG.MAX + ' cm.');
    }
    if (Number.isInteger(l) && (l < CONFIG.MIN || l > CONFIG.MAX)) {
      errors.push('Length must be between ' + CONFIG.MIN + ' and ' + CONFIG.MAX + ' cm.');
    }

    return errors;
  }

  /**
   * Intercept form submit in capture phase — blocks Easify AND the theme's
   * product-form handler. We submit directly via the Cart AJAX API instead.
   */
  function onSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (isSubmitting) return;

    syncValues();

    var errors = validate();
    if (errors.length > 0) {
      showErrors(errors);
      return;
    }

    clearErrors();
    submitToCart();
  }

  function submitToCart() {
    isSubmitting = true;
    var submitBtn = formEl.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

    // Get variant ID from the form (handles both direct and variant-picker cases)
    var formData = new FormData(formEl);
    var variantId = formData.get('id');
    var quantity = parseInt(quantityInput.value, 10);
    var lineProperties = {
      'Width (cm)': widthInput.value,
      'Length (cm)': lengthInput.value,
      '_sheet_metal': 'true'
    };
    var previousLine = null;

    // Collect section IDs that components need rendered (e.g., cart drawer)
    var sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    fetchCart()
    .then(function (cart) {
      previousLine = findMatchingSheetMetalLine(cart, variantId, lineProperties);

      return fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: quantity,
          properties: lineProperties,
          sections: sectionsToBundle.join(','),
          sections_url: Shopify.routes.root + 'variants/' + variantId
        })
      })
      .then(function (response) {
        return response.json().then(function (data) {
          return { response: response, data: data };
        });
      })
      .catch(function (error) {
        // A rejected request or unreadable response is ambiguous: Shopify may
        // have changed the cart before the browser lost the response.
        error.isAmbiguousSheetMetalAdd = true;
        throw error;
      });
    })
    .then(function (result) {
      var response = result.response;
      var data = result.data;

      if (response.ok) {
        // Cart page mode: redirect
        if (window.themeVariables && window.themeVariables.settings.cartType === 'page') {
          window.location.href = Shopify.routes.root + 'cart';
          return;
        }

        // Cart drawer mode: fetch cart and dispatch theme events
        return fetchCart()
          .then(function (cartContent) {
            // Include rendered section HTML from the add response
            cartContent.sections = data.sections;
            // Adjust item_count so the badge shows number of line items,
            // not the sum of area quantities for sheet metal items
            cartContent.item_count = adjustCartCount(cartContent);

            formEl.dispatchEvent(new CustomEvent('variant:add', {
              bubbles: true,
              detail: {
                items: data.hasOwnProperty('items') ? data.items : [data],
                cart: cartContent
              }
            }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                cart: cartContent
              }
            }));
          });
      }

      var msg = data.description || data.message || '';

      // Shopify's inventory error wording can change. Compare the cart with
      // its pre-request state and roll back only when this exact line grew.
      return reconcileFailedAdd(previousLine, variantId, lineProperties, sectionsToBundle)
        .then(function (reconciliation) {
          if (reconciliation.rolledBack) {
            dispatchCartChange(reconciliation.cart, 'sheet-metal:rollback');
            showErrors([INSUFFICIENT_STOCK_ERROR]);
            return;
          }

          showErrors([msg || 'Could not add to cart.']);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }, function (error) {
          error.isSheetMetalRollbackError = true;
          throw error;
        });
    })
    .then(function () {
      resetSubmissionState(submitBtn);
    })
    .catch(function (error) {
      if (error && error.isAmbiguousSheetMetalAdd) {
        return reconcileFailedAdd(previousLine, variantId, lineProperties, sectionsToBundle)
          .then(function (reconciliation) {
            if (reconciliation.rolledBack) {
              dispatchCartChange(reconciliation.cart, 'sheet-metal:rollback');
            } else {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }

            showErrors(['Something went wrong. Please try again.']);
          }, function () {
            showErrors([ROLLBACK_ERROR]);
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          })
          .then(function () {
            resetSubmissionState(submitBtn);
          });
      }

      resetSubmissionState(submitBtn);
      showErrors([error && error.isSheetMetalRollbackError ? ROLLBACK_ERROR : 'Something went wrong. Please try again.']);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    });
  }

  function fetchCart() {
    return fetch(Shopify.routes.root + 'cart.js')
      .then(function (response) {
        if (!response.ok) throw new Error('Could not read cart.');
        return response.json();
      });
  }

  function findMatchingSheetMetalLine(cart, variantId, properties) {
    if (!cart || !cart.items) return null;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var itemProperties = item.properties || {};

      if (
        String(item.variant_id || item.id) === String(variantId) &&
        String(itemProperties['Width (cm)']) === String(properties['Width (cm)']) &&
        String(itemProperties['Length (cm)']) === String(properties['Length (cm)']) &&
        itemProperties._sheet_metal === 'true'
      ) {
        return item;
      }
    }

    return null;
  }

  function findCartLineByKey(cart, lineKey) {
    if (!cart || !cart.items || !lineKey) return null;

    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === lineKey) return cart.items[i];
    }

    return null;
  }

  function reconcileFailedAdd(previousLine, variantId, properties, sectionsToBundle) {
    var previousQuantity = previousLine ? previousLine.quantity : 0;

    return fetchCart()
      .then(function (cart) {
        var currentLine = previousLine
          ? findCartLineByKey(cart, previousLine.key) || findMatchingSheetMetalLine(cart, variantId, properties)
          : findMatchingSheetMetalLine(cart, variantId, properties);

        if (!currentLine || currentLine.quantity <= previousQuantity) {
          return { cart: cart, rolledBack: false };
        }

        return fetch(Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: currentLine.key,
            quantity: previousQuantity,
            sections: sectionsToBundle.join(','),
            sections_url: Shopify.routes.root + 'variants/' + variantId
          })
        })
        .then(function (response) {
          if (!response.ok) throw new Error('Could not restore the sheet-metal line.');
          return response.json();
        })
        .then(function (restoredCart) {
          var restoredLine = findCartLineByKey(restoredCart, currentLine.key) ||
            findMatchingSheetMetalLine(restoredCart, variantId, properties);

          if (
            (previousQuantity === 0 && restoredLine) ||
            (previousQuantity > 0 && (!restoredLine || restoredLine.quantity !== previousQuantity))
          ) {
            throw new Error('The sheet-metal line was not restored to its previous quantity.');
          }

          return { cart: restoredCart, rolledBack: true };
        });
      });
  }

  function dispatchCartChange(cart, baseEvent) {
    cart.item_count = adjustCartCount(cart);
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: baseEvent,
        cart: cart
      }
    }));
  }

  function resetSubmissionState(submitBtn) {
    isSubmitting = false;
    if (submitBtn) submitBtn.removeAttribute('aria-busy');
  }

  /**
   * Count sheet metal line items as 1 (not their area quantity) so the
   * cart badge shows the real number of items, not the sum of cm².
   */
  function adjustCartCount(cart) {
    var count = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties;
      if (props && props._sheet_metal === 'true') {
        count += 1;
      } else {
        count += cart.items[i].quantity;
      }
    }
    return count;
  }

  function showErrors(errors) {
    clearErrors();

    var banner = document.createElement('div');
    banner.className = 'banner banner--error justify-center sheet-metal-error';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'polite');
    banner.style.cssText = 'grid-column:1/-1;margin-bottom:1rem;';
    banner.innerHTML =
      '<svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-error" style="--icon-height:18px" viewBox="0 0 18 18">' +
        '<path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>' +
        '<path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>' +
      '</svg>' +
      '<p></p>';
    banner.querySelector('p').textContent = errors.join(' ');

    var buyButtons = formEl.querySelector('buy-buttons');
    if (buyButtons) {
      buyButtons.before(banner);
    } else {
      formEl.prepend(banner);
    }
  }

  function clearErrors() {
    var existing = formEl.querySelector('.sheet-metal-error');
    if (existing) existing.remove();
  }

  }

  function start() {
    init(document);

    if (!document.body || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var node = records[i].addedNodes[j];
          if (node.nodeType === 1) init(node);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.KernowcraftSheetMetalController = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
